// ⚠️ FICHIER GÉNÉRÉ — ne pas éditer ici. Source : shared/ui/ · régénérer avec `bash shared/sync.sh`
/**
 * LE POSTE DU JOUR, DIT SUR L'ÉCRAN DE RECONNAISSANCE — module PUR.
 * ───────────────────────────────────────────────────────────────────────────
 *
 * ── La demande du 27/08/2026 ────────────────────────────────────────────────
 *
 * La RQ, à propos de « Aujourd’hui, RMQ · 07:00–15:00 » : « je voudrais que
 * cela s'affiche uniquement au moment du login. Il y a un moment où, au login,
 * s'affiche la reconnaissance de l'identité et du rôle. L'afficher uniquement
 * là-bas avec les données qui existaient déjà. »
 *
 * L'écran de connexion disait déjà « Connexion réussie — votre profil a été
 * reconnu automatiquement », avec la pastille du rôle. Le mémo du tableau de
 * bord disait « عسلامة Inès » et l'horaire du jour. Les deux répondent à la
 * même question — QUI suis-je pour l'application, et pour quoi faire — à trois
 * secondes d'intervalle. Elles se rejoignent donc au seul endroit où la
 * question se pose vraiment : à l'entrée.
 *
 * ── Pourquoi ce calcul n'est pas laissé au serveur ──────────────────────────
 *
 * La route rend DÉJÀ une phrase toute faite (`phrase`), et elle reste la bonne
 * réponse pour les cas particuliers — congé, aucun poste. Mais la pastille est
 * déjà intitulée « Aujourd’hui » : y répéter « Aujourd’hui, RMQ · 07:00–15:00 »
 * écrirait le mot deux fois. On ne demande pas au serveur une seconde phrase
 * qui ne différerait que par son début : on garde la sienne pour ce qu'elle
 * sait dire, et on compose ici la forme courte, à partir des mêmes postes.
 *
 * ⚠️ CE MODULE NE DÉCIDE PAS SI L'ON AFFICHE — `aQuelqueChoseADire` le dit, et
 * séparément. Rendre une chaîne vide comme signal d'absence marche jusqu'au
 * jour où un libellé de poste est vide en base : l'écran affiche alors une
 * pastille creuse, et personne ne sait si c'est un poste sans nom ou pas de
 * poste du tout.
 */

/** Reflet de `GET /ged/rh/mon-jour` — mêmes noms qu'au serveur, pour qu'on
 *  puisse suivre la donnée d'un bout à l'autre sans table de correspondance. */
export interface AccueilDuJour {
  prenom: string;
  /** Phrase construite par le serveur : congé, aucun poste, ou n postes. */
  phrase: string;
  enConge: boolean;
  postes: { code: string; libelle: string; heureDebut: string; heureFin: string }[];
}

/**
 * `07:00:00.000` → `07:00`.
 *
 * ⚠️ Les heures viennent de deux colonnes distinctes — l'horaire de
 * l'affectation, à défaut la plage du poste. Une colonne `time` rend les
 * secondes et les millisecondes ; l'autre est saisie à la main. Tronquer ici
 * évite qu'un « 07:00:00.000–15:00:00.000 » passe un jour à l'écran d'accueil
 * sans que rien ne soit tombé en panne.
 */
export function hhmm(v: string | null | undefined): string {
  const s = String(v ?? '').trim();
  return /^\d{1,2}:\d{2}/.test(s) ? s.slice(0, s.indexOf(':') + 3) : s;
}

/** Un poste : « RMQ · 07:00–15:00 », ou son seul libellé si l'horaire manque. */
function unPoste(p: AccueilDuJour['postes'][number]): string {
  const nom = String(p.libelle || p.code || '').trim();
  const d = hhmm(p.heureDebut);
  const f = hhmm(p.heureFin);
  /* Une heure de fin sans heure de début — ou l'inverse — ne compose pas un
     intervalle : on rend alors le nom seul plutôt qu'un « –15:00 » orphelin. */
  return d && f ? `${nom} · ${d}–${f}` : nom;
}

/**
 * Y a-t-il quelque chose à dire de cette journée ?
 *
 * ⚠️ « Aucun poste ne vous est affecté aujourd’hui » n'est PAS affiché à la
 * connexion. La route répond la même chose à qui n'est pas au planning ce
 * matin et à qui n'a aucune fiche RH — l'administrateur informatique, un
 * confrère. Servir cette phrase à tout le monde, tous les jours, apprend à ne
 * plus lire la pastille ; celui qui attendait un poste s'en aperçoit de toute
 * façon en ouvrant son planning, où l'absence est un fait et non une devinette.
 */
export function aQuelqueChoseADire(a: AccueilDuJour | null | undefined): boolean {
  return !!a && (a.enConge || a.postes.length > 0);
}

/**
 * La forme courte, pour la pastille déjà intitulée « Aujourd’hui ».
 *
 * Le congé rend la phrase DU SERVEUR : c'est une situation, pas un horaire, et
 * elle est déjà écrite à un seul endroit.
 */
export function libelleDuJour(a: AccueilDuJour): string {
  if (a.enConge) return a.phrase;
  return a.postes.map(unPoste).filter(Boolean).join('  ·  ');
}
